import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  BadgeDollarSign,
  CalendarClock,
  CreditCard,
  FileText,
  Gift,
  Globe2,
  History,
  Loader2,
  MapPin,
  PencilLine,
  Plus,
  ReceiptText,
  Search,
  ShieldCheck,
  UserRound,
  X
} from 'lucide-react';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import PageHeader from '../../components/common/PageHeader';
import { ADMIN_PERMISSIONS } from '../../constants';
import { adminAPI } from '../../services/api';
import useAuthStore from '../../store/authStore';
import { hasPermission } from '../../utils/rbac';

const tabs = [
  { id: 'overview', label: 'Overview', icon: ShieldCheck },
  { id: 'plans', label: 'Plan pricing', icon: BadgeDollarSign },
  { id: 'candidates', label: 'Subscriptions', icon: UserRound },
  { id: 'offers', label: 'Offers', icon: Gift },
  { id: 'payments', label: 'Transactions', icon: ReceiptText }
];

const defaultFilters = {
  search: '',
  region: '',
  status: '',
  page: 1,
  limit: 25
};

const emptyOfferForm = {
  offerName: '',
  description: '',
  scope: 'global',
  userId: '',
  region: '',
  planId: '',
  amount: '',
  currency: 'USD',
  durationDays: 30,
  validUntil: '',
  maxRedemptions: ''
};

const emptyExtensionForm = {
  userId: '',
  planId: '',
  durationDays: 30,
  notes: ''
};

const emptyPriceForm = {
  amount: '',
  currency: 'USD',
  effectiveAt: '',
  reason: ''
};

const extractError = (error, fallback) => (
  error?.response?.data?.error ||
  error?.response?.error ||
  error?.message ||
  fallback
);
const formatReference = (value) => value ? String(value).split('-')[0].toUpperCase() : 'N/A';

const formatNumber = (value) => new Intl.NumberFormat('en-US', {
  notation: Math.abs(Number(value) || 0) >= 10000 ? 'compact' : 'standard',
  maximumFractionDigits: 0
}).format(Number(value) || 0);

const formatMoney = (amountCents, currency = 'USD') => {
  const safeCurrency = String(currency || 'USD').trim().toUpperCase() || 'USD';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: safeCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format((Number(amountCents) || 0) / 100);
  } catch (_error) {
    return `${safeCurrency} ${((Number(amountCents) || 0) / 100).toFixed(2)}`;
  }
};

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

const formatDateInput = (date) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return '';
  const timezoneOffset = value.getTimezoneOffset() * 60000;
  return new Date(value.getTime() - timezoneOffset).toISOString().slice(0, 16);
};

const labelize = (value = '') => String(value || 'Unknown')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase())
  .replace(/\bKyc\b/g, 'KYC')
  .replace(/\bUsd\b/g, 'USD')
  .replace(/\bGbp\b/g, 'GBP');

const statusTone = (status) => {
  if (status === 'paid' || status === 'active' || status === 'applied') return 'emerald';
  if (status === 'trial' || status === 'extended') return 'cyan';
  if (status === 'expired' || status === 'suspended' || status === 'cancelled' || status === 'pricing_superseded') return 'rose';
  if (status === 'paused' || status === 'pending' || status === 'scheduled') return 'amber';
  return 'slate';
};

const toneClasses = {
  slate: 'border-slate-200 bg-slate-50 text-slate-600',
  cyan: 'border-cyan-200 bg-cyan-50 text-cyan-700',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  amber: 'border-amber-200 bg-amber-50 text-amber-700',
  rose: 'border-rose-200 bg-rose-50 text-rose-700'
};

const Chip = ({ children, tone = 'slate' }) => (
  <span className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-semibold ${toneClasses[tone] || toneClasses.slate}`}>
    {children}
  </span>
);

const MetricCard = ({ icon: Icon, label, value, detail, tone = 'cyan' }) => {
  const badgeClasses = {
    cyan: 'bg-cyan-50 text-cyan-600 border-cyan-100/50',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100/50',
    amber: 'bg-amber-50 text-amber-600 border-amber-100/50',
    rose: 'bg-rose-50 text-rose-600 border-rose-100/50',
    slate: 'bg-slate-50 text-slate-600 border-slate-100/50'
  }[tone] || 'bg-cyan-50 text-cyan-600 border-cyan-100/50';

  return (
    <article className="bg-white rounded-[24px] p-6 shadow-sm border border-slate-100 flex flex-col justify-between hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-6">
        <span className="text-sm font-semibold text-slate-600">{label}</span>
        {Icon && (
          <div className={`w-10 h-10 rounded-2xl flex items-center justify-center border shrink-0 ${badgeClasses}`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
      <div>
        <p className="text-3xl font-bold tracking-tight text-slate-900">{value}</p>
        {detail && <p className="mt-2 text-xs font-medium text-slate-400">{detail}</p>}
      </div>
    </article>
  );
};

const Panel = ({ title, subtitle, icon: Icon, actions, children }) => (
  <section className="rounded-[24px] border border-slate-100 bg-white shadow-sm flex flex-col overflow-hidden">
    <div className="flex flex-col gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-start sm:justify-between bg-white">
      <div className="flex min-w-0 items-start gap-3">
        {Icon && (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-cyan-100/50 bg-cyan-50 text-cyan-600">
            <Icon size={20} />
          </span>
        )}
        <div className="min-w-0">
          <h2 className="text-[17px] font-semibold leading-tight text-slate-900">{title}</h2>
          {subtitle && <p className="mt-1 text-xs leading-5 text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="shrink-0">{actions}</div>}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const BillingSkeleton = () => (
  <div className="min-h-full px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8" aria-label="Loading billing">
    <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
      <div className="space-y-3">
        <SkeletonBlock className="h-4 w-44 rounded-md" />
        <SkeletonBlock className="h-10 w-72 rounded-lg" />
        <SkeletonBlock className="h-4 w-[44rem] max-w-full rounded-md" />
      </div>
      <SkeletonBlock className="h-10 w-28 rounded-lg" />
    </div>
    <div className="mb-5 flex gap-2 overflow-hidden">
      {tabs.map((tab) => <SkeletonBlock key={tab.id} className="h-10 w-36 rounded-lg" />)}
    </div>
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 8 }).map((_, index) => <SkeletonBlock key={index} className="h-28 rounded-lg" />)}
    </div>
    <div className="mt-5 grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
      <SkeletonBlock className="h-[520px] rounded-lg" />
      <SkeletonBlock className="h-[520px] rounded-lg" />
    </div>
  </div>
);

const Modal = ({ title, children, onClose, width = 'max-w-3xl' }) => (
  <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/50 px-4 py-8">
    <section className={`w-full ${width} rounded-[24px] bg-white shadow-sm border border-slate-200 overflow-hidden`}>
      <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-5">
        <h2 className="text-[17px] font-semibold leading-tight text-slate-900">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[16px] border border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
        >
          <X size={17} />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </section>
  </div>
);

const EmptyState = ({ children }) => (
  <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-5 py-8 text-center text-sm leading-6 text-slate-500">
    {children}
  </div>
);

const CandidateDetailModal = ({ userId, canManageBilling, onClose, onExtend }) => {
  const detailQuery = useQuery(
    ['candidate-billing-detail', userId],
    () => adminAPI.getCandidateBillingDetail(userId),
    { enabled: Boolean(userId) }
  );

  return (
    <Modal title="Candidate billing detail" onClose={onClose} width="max-w-5xl">
      {detailQuery.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, index) => <SkeletonBlock key={index} className="h-20 rounded-lg" />)}
        </div>
      ) : detailQuery.isError ? (
        <EmptyState>{extractError(detailQuery.error, 'Candidate billing detail could not be loaded.')}</EmptyState>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-col gap-4 rounded-[24px] border border-slate-100 bg-slate-50 p-6 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="text-xl font-semibold text-slate-900">{detailQuery.data?.candidate?.name}</h3>
              <p className="mt-1 text-sm text-slate-500">{detailQuery.data?.candidate?.email}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Chip tone={statusTone(detailQuery.data?.state?.billingStatus)}>{labelize(detailQuery.data?.state?.billingStatus)}</Chip>
                <Chip>{detailQuery.data?.candidate?.location || 'No location'}</Chip>
                <Chip>{detailQuery.data?.state?.plan?.name || 'No plan'}</Chip>
              </div>
            </div>
            {canManageBilling && (
              <button
                type="button"
                onClick={() => onExtend(detailQuery.data?.candidate)}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                <CalendarClock size={16} />
                Extend access
              </button>
            )}
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Panel title="Subscriptions" icon={CreditCard}>
              <div className="space-y-3">
                {(detailQuery.data?.subscriptions || []).map((subscription) => (
                  <div key={subscription.id} className="rounded-[24px] border border-slate-100 bg-slate-50 p-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold text-slate-800">{subscription.planName}</span>
                      <Chip tone={statusTone(subscription.status)}>{labelize(subscription.status)}</Chip>
                    </div>
                    <p className="mt-2 text-slate-500">Period end: {formatDateTime(subscription.currentPeriodEnd || subscription.trialEnd)}</p>
                  </div>
                ))}
                {!detailQuery.data?.subscriptions?.length && <EmptyState>No subscriptions recorded.</EmptyState>}
              </div>
            </Panel>

            <Panel title="Grants & Extensions" icon={CalendarClock}>
              <div className="space-y-3">
                {(detailQuery.data?.grants || []).map((grant) => (
                  <div key={grant.id} className="rounded-[24px] border border-slate-100 bg-slate-50 p-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold text-slate-800">{labelize(grant.type)}</span>
                      <Chip tone={statusTone(grant.status)}>{labelize(grant.status)}</Chip>
                    </div>
                    <p className="mt-2 text-slate-500">Ends: {formatDateTime(grant.endsAt)}</p>
                    {grant.notes && <p className="mt-2 text-xs leading-5 text-slate-500">{grant.notes}</p>}
                  </div>
                ))}
                {!detailQuery.data?.grants?.length && <EmptyState>No grants or extensions recorded.</EmptyState>}
              </div>
            </Panel>

            <Panel title="Offer History" icon={Gift}>
              <div className="space-y-3">
                {(detailQuery.data?.offers || []).map((offer) => (
                  <div key={offer.id} className="rounded-[24px] border border-slate-100 bg-slate-50 p-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold text-slate-800">{offer.offerName}</span>
                      <Chip tone={statusTone(offer.offerStatus)}>{labelize(offer.offerStatus)}</Chip>
                    </div>
                    <p className="mt-2 text-slate-500">{formatMoney(offer.amountCents, offer.currency)} · {offer.durationDays} days</p>
                  </div>
                ))}
                {!detailQuery.data?.offers?.length && <EmptyState>No offers sent to this candidate.</EmptyState>}
              </div>
            </Panel>
          </div>

          <Panel title="Payments" icon={ReceiptText}>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50/50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-4">Reference</th>
                    <th className="px-6 py-4">Plan</th>
                    <th className="px-6 py-4">Amount</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(detailQuery.data?.payments || []).map((payment) => (
                    <tr key={payment.id}>
                      <td className="px-6 py-4 font-mono text-xs text-slate-600">{payment.reference}</td>
                      <td className="px-6 py-4 text-slate-700">{payment.planName || 'Plan unavailable'}</td>
                      <td className="px-6 py-4 font-semibold text-slate-900">{formatMoney(payment.amountCents, payment.currency)}</td>
                      <td className="px-6 py-4"><Chip tone={statusTone(payment.status)}>{labelize(payment.status)}</Chip></td>
                      <td className="px-6 py-4 text-slate-500">{formatDateTime(payment.paidAt || payment.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!detailQuery.data?.payments?.length && <EmptyState>No payments recorded.</EmptyState>}
          </Panel>
        </div>
      )}
    </Modal>
  );
};

const ExtensionModal = ({ candidate, plans, onClose, onSaved }) => {
  const [form, setForm] = useState({
    ...emptyExtensionForm,
    userId: candidate?.userId || candidate?.id || '',
    planId: plans.find((plan) => plan.active)?.id || plans[0]?.id || '',
    notes: 'Administrative billing access extension'
  });
  const [saving, setSaving] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await adminAPI.extendCandidateBillingAccess({
        ...form,
        userId: Number(form.userId),
        planId: Number(form.planId),
        durationDays: Number(form.durationDays)
      });
      toast.success('Candidate access extended.');
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(extractError(error, 'Access extension failed.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Extend access${candidate?.name ? ` for ${candidate.name}` : ''}`} onClose={onClose} width="max-w-xl">
      {!plans.length ? (
        <EmptyState>No active billing plan is available. Configure a candidate billing plan before extending access.</EmptyState>
      ) : (
      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <span className="mb-2 block text-sm font-medium text-slate-700">Candidate user ID</span>
          <input
            value={form.userId}
            onChange={(event) => setForm((current) => ({ ...current, userId: event.target.value }))}
            className="h-11 w-full rounded-[16px] border border-slate-200 px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
            required
          />
        </label>
        <label className="block">
          <span className="mb-2 block text-sm font-medium text-slate-700">Plan</span>
          <select
            value={form.planId}
            onChange={(event) => setForm((current) => ({ ...current, planId: event.target.value }))}
            className="h-11 w-full rounded-[16px] border border-slate-200 bg-white px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
            required
          >
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>{plan.name} · {formatMoney(plan.amountCents, plan.currency)}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-2 block text-sm font-medium text-slate-700">Duration in days</span>
          <input
            type="number"
            min="1"
            max="3650"
            value={form.durationDays}
            onChange={(event) => setForm((current) => ({ ...current, durationDays: event.target.value }))}
            className="h-11 w-full rounded-[16px] border border-slate-200 px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
            required
          />
        </label>
        <label className="block">
          <span className="mb-2 block text-sm font-medium text-slate-700">Reason or notes</span>
          <textarea
            value={form.notes}
            onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
            className="min-h-[96px] w-full rounded-[16px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
          />
        </label>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <CalendarClock size={16} />}
          Extend access
        </button>
      </form>
      )}
    </Modal>
  );
};

const OfferModal = ({ plans, seedCandidate, onClose, onSaved }) => {
  const defaultValidUntil = formatDateInput(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000));
  const defaultPlan = plans.find((plan) => plan.active) || plans[0];
  const [form, setForm] = useState({
    ...emptyOfferForm,
    scope: seedCandidate ? 'user' : 'global',
    userId: seedCandidate?.userId || '',
    planId: defaultPlan?.id || '',
    amount: defaultPlan ? String((Number(defaultPlan.amountCents || 0) / 100).toFixed(2)) : '',
    currency: defaultPlan?.currency || 'USD',
    validUntil: defaultValidUntil
  });
  const [saving, setSaving] = useState(false);

  const selectedPlan = useMemo(() => plans.find((plan) => Number(plan.id) === Number(form.planId)), [plans, form.planId]);

  useEffect(() => {
    if (!selectedPlan) return;
    setForm((current) => ({
      ...current,
      currency: current.currency || selectedPlan.currency || 'USD'
    }));
  }, [selectedPlan]);

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await adminAPI.createBillingOffer({
        ...form,
        planId: Number(form.planId),
        amount: Number(form.amount),
        durationDays: Number(form.durationDays),
        maxRedemptions: form.maxRedemptions ? Number(form.maxRedemptions) : null,
        validUntil: new Date(form.validUntil).toISOString()
      });
      toast.success('Billing offer created and notifications queued.');
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(extractError(error, 'Billing offer creation failed.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Create billing offer" onClose={onClose} width="max-w-3xl">
      {!plans.length ? (
        <EmptyState>No active billing plan is available. Configure a candidate billing plan before creating billing offers.</EmptyState>
      ) : (
      <form onSubmit={submit} className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-slate-700">Offer name</span>
            <input
              value={form.offerName}
              onChange={(event) => setForm((current) => ({ ...current, offerName: event.target.value }))}
              placeholder="June candidate access offer"
              className="h-11 w-full rounded-[16px] border border-slate-200 px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
              required
            />
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-slate-700">Scope</span>
            <select
              value={form.scope}
              onChange={(event) => setForm((current) => ({ ...current, scope: event.target.value }))}
              className="h-11 w-full rounded-[16px] border border-slate-200 bg-white px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
            >
              <option value="global">Global</option>
              <option value="region">Region</option>
              <option value="user">Single candidate</option>
            </select>
          </label>
          {form.scope === 'region' && (
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-700">Region</span>
              <input
                value={form.region}
                onChange={(event) => setForm((current) => ({ ...current, region: event.target.value }))}
                placeholder="Kenya, Nairobi, United States"
                className="h-11 w-full rounded-[16px] border border-slate-200 px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
                required
              />
            </label>
          )}
          {form.scope === 'user' && (
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-700">Candidate user ID</span>
              <input
                value={form.userId}
                onChange={(event) => setForm((current) => ({ ...current, userId: event.target.value }))}
                className="h-11 w-full rounded-[16px] border border-slate-200 px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
                required
              />
            </label>
          )}
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-slate-700">Plan</span>
            <select
              value={form.planId}
              onChange={(event) => setForm((current) => ({ ...current, planId: event.target.value }))}
              className="h-11 w-full rounded-[16px] border border-slate-200 bg-white px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
              required
            >
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>{plan.name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-slate-700">Amount</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))}
              className="h-11 w-full rounded-[16px] border border-slate-200 px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
              required
            />
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-slate-700">Currency</span>
            <select
              value={form.currency}
              onChange={(event) => setForm((current) => ({ ...current, currency: event.target.value }))}
              className="h-11 w-full rounded-[16px] border border-slate-200 bg-white px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
              required
            >
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
            </select>
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-slate-700">Access duration</span>
            <input
              type="number"
              min="1"
              max="3650"
              value={form.durationDays}
              onChange={(event) => setForm((current) => ({ ...current, durationDays: event.target.value }))}
              className="h-11 w-full rounded-[16px] border border-slate-200 px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
              required
            />
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-slate-700">Valid until</span>
            <input
              type="datetime-local"
              value={form.validUntil}
              onChange={(event) => setForm((current) => ({ ...current, validUntil: event.target.value }))}
              className="h-11 w-full rounded-[16px] border border-slate-200 px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
              required
            />
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-slate-700">Max redemptions</span>
            <input
              type="number"
              min="1"
              value={form.maxRedemptions}
              onChange={(event) => setForm((current) => ({ ...current, maxRedemptions: event.target.value }))}
              placeholder="No limit"
              className="h-11 w-full rounded-[16px] border border-slate-200 px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
            />
          </label>
        </div>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-slate-700">Description sent to candidates</span>
          <textarea
            value={form.description}
            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
            className="min-h-[110px] w-full rounded-[16px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
            placeholder="Explain who the offer is for, what it unlocks, and the validity window."
          />
        </label>

        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm leading-6 text-amber-800">
          Creating an offer inserts candidate notifications with push enabled and queues emails for the matching recipients.
        </div>

        <button
          type="submit"
          disabled={saving}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Gift size={16} />}
          Create offer
        </button>
      </form>
      )}
    </Modal>
  );
};

const PlanPriceModal = ({ plan, onClose, onSaved }) => {
  const [form, setForm] = useState({
    ...emptyPriceForm,
    amount: plan ? String((Number(plan.amountCents || 0) / 100).toFixed(2)) : '',
    currency: plan?.currency || 'USD',
    effectiveAt: formatDateInput(new Date()),
    reason: ''
  });
  const [saving, setSaving] = useState(false);

  const effectiveDate = form.effectiveAt ? new Date(form.effectiveAt) : null;
  const isFuturePrice = effectiveDate && !Number.isNaN(effectiveDate.getTime()) && effectiveDate.getTime() > Date.now() + 30000;

  const submit = async (event) => {
    event.preventDefault();
    if (saving || !plan?.id) return;
    setSaving(true);
    try {
      await adminAPI.updateBillingPlanPrice(plan.id, {
        amount: Number(form.amount),
        currency: form.currency,
        effectiveAt: new Date(form.effectiveAt).toISOString(),
        reason: form.reason
      });
      toast.success(isFuturePrice ? 'Plan price scheduled.' : 'Plan price updated.');
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(extractError(error, 'Plan price update failed.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Change price for ${plan?.name || 'billing plan'}`} onClose={onClose} width="max-w-xl">
      <form onSubmit={submit} className="space-y-4">
        <div className="rounded-[16px] border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-semibold text-slate-900">Current active price</p>
          <p className="mt-1 text-2xl font-semibold text-slate-900">{formatMoney(plan?.amountCents, plan?.currency)}</p>
          <p className="mt-1 text-sm text-slate-500">
            Used by new candidate payment sessions once the effective date is reached.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_120px]">
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-slate-700">New amount</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))}
              className="h-11 w-full rounded-[16px] border border-slate-200 px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
              required
            />
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-slate-700">Currency</span>
            <select
              value={form.currency}
              onChange={(event) => setForm((current) => ({ ...current, currency: event.target.value }))}
              className="h-11 w-full rounded-[16px] border border-slate-200 bg-white px-3 text-sm font-semibold uppercase text-slate-700 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
              required
            >
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
            </select>
          </label>
        </div>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-slate-700">Effective date and time</span>
          <input
            type="datetime-local"
            value={form.effectiveAt}
            onChange={(event) => setForm((current) => ({ ...current, effectiveAt: event.target.value }))}
            className="h-11 w-full rounded-[16px] border border-slate-200 px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
            required
          />
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-slate-700">Reason</span>
          <textarea
            value={form.reason}
            onChange={(event) => setForm((current) => ({ ...current, reason: event.target.value }))}
            placeholder="Commercial approval, launch pricing, renewal adjustment, or finance instruction."
            className="min-h-[96px] w-full rounded-[16px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
          />
        </label>

        <div className={`rounded-lg border p-3 text-sm leading-6 ${
          isFuturePrice
            ? 'border-amber-200 bg-amber-50 text-amber-800'
            : 'border-emerald-200 bg-emerald-50 text-emerald-800'
        }`}>
          {isFuturePrice
            ? 'This price will be scheduled. The current price remains active until the effective time.'
            : 'This price will apply immediately. Existing pending checkout links for this plan will be superseded.'}
        </div>

        <button
          type="submit"
          disabled={saving}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <BadgeDollarSign size={16} />}
          {isFuturePrice ? 'Schedule price' : 'Apply price'}
        </button>
      </form>
    </Modal>
  );
};

function BillingPage() {
  const user = useAuthStore((state) => state.user);
  const canManageBilling = hasPermission(user, ADMIN_PERMISSIONS.BILLING_MANAGE);
  const [activeTab, setActiveTab] = useState('overview');
  const [filters, setFilters] = useState(defaultFilters);
  const [filterDraft, setFilterDraft] = useState(defaultFilters);
  const [detailUserId, setDetailUserId] = useState(null);
  const [extensionCandidate, setExtensionCandidate] = useState(null);
  const [offerSeedCandidate, setOfferSeedCandidate] = useState(null);
  const [pricePlan, setPricePlan] = useState(null);
  const [updatingOfferId, setUpdatingOfferId] = useState(null);

  const billingQuery = useQuery(
    ['admin-billing', filters],
    () => adminAPI.getBilling(filters),
    { keepPreviousData: true, staleTime: 30000 }
  );

  const data = billingQuery.data || {};
  const summary = data.summary || {};
  const plans = data.plans || [];
  const candidates = data.candidates || [];
  const offers = data.offers || [];
  const recentPayments = data.recentPayments || [];
  const pagination = data.pagination || { page: 1, limit: 25, total: 0, totalPages: 1 };
  const revenueTotal = (summary.revenueByCurrency || []).reduce((total, item) => total + Number(item.amountCents30d || 0), 0);
  const revenueCurrency = summary.revenueByCurrency?.[0]?.currency || 'USD';

  const refresh = () => billingQuery.refetch();

  const submitFilters = (event) => {
    event.preventDefault();
    setFilters({
      ...filterDraft,
      page: 1
    });
    setActiveTab('candidates');
  };

  const changePage = (page) => {
    setFilters((current) => ({ ...current, page }));
    setFilterDraft((current) => ({ ...current, page }));
  };

  const updateOfferStatus = async (offer, status) => {
    setUpdatingOfferId(offer.id);
    try {
      await adminAPI.updateBillingOfferStatus(offer.id, { status });
      toast.success('Offer status updated.');
      await refresh();
    } catch (error) {
      toast.error(extractError(error, 'Offer status update failed.'));
    } finally {
      setUpdatingOfferId(null);
    }
  };

  if (billingQuery.isLoading) return <BillingSkeleton />;

  return (
    <div className="min-h-full px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
      <PageHeader
        title="Billing"
        description="Monitor candidate billing status, payments, access expiry, administrative extensions, and billing offers by user, region, or globally."
        actions={canManageBilling && (
          <>
            <button
              type="button"
              onClick={() => setPricePlan(plans.find((plan) => plan.active) || plans[0])}
              disabled={!plans.length}
              className="inline-flex items-center justify-center gap-2 rounded-[16px] border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <PencilLine size={16} />
              Change price
            </button>
            <button
              type="button"
              onClick={() => setExtensionCandidate({})}
              disabled={!plans.length}
              className="inline-flex items-center justify-center gap-2 rounded-[16px] border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <CalendarClock size={16} />
              Extend access
            </button>
            <button
              type="button"
              onClick={() => setOfferSeedCandidate({})}
              disabled={!plans.length}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus size={16} />
              Create offer
            </button>
          </>
        )}
      />

      {billingQuery.isError && (
        <div className="mb-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {extractError(billingQuery.error, 'Unable to load billing.')}
        </div>
      )}

      {!canManageBilling && (
        <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
          You have billing view access. Offer creation and access extensions require <span className="font-semibold">billing.manage</span>.
        </div>
      )}

      <nav className="mb-5 flex gap-2 overflow-x-auto pb-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex shrink-0 items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition ${
                active
                  ? 'border-cyan-200 bg-cyan-50 text-cyan-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-cyan-200 hover:text-cyan-700'
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </nav>

      {activeTab === 'overview' && (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard icon={UserRound} label="Candidates" value={formatNumber(summary.totalCandidates)} detail={`${formatNumber(summary.activeCandidates)} active accounts`} />
            <MetricCard icon={BadgeDollarSign} label="Paid active" value={formatNumber(summary.paidActiveCandidates)} detail="Candidates with active paid subscriptions" tone="emerald" />
            <MetricCard icon={CalendarClock} label="Trials & extensions" value={formatNumber((summary.activeTrials || 0) + (summary.activeExtensions || 0))} detail={`${formatNumber(summary.expiringSoon)} expiring within 7 days`} tone="cyan" />
            <MetricCard icon={Gift} label="Active offers" value={formatNumber(summary.activeOffers)} detail={`${formatNumber(summary.pausedOffers)} paused · ${formatNumber(summary.cancelledOffers)} cancelled`} tone="amber" />
            <MetricCard icon={ReceiptText} label="Revenue 30d" value={formatMoney(revenueTotal, revenueCurrency)} detail="Paid transactions in the last 30 days" tone="emerald" />
            <MetricCard icon={AlertCircle} label="Needs attention" value={formatNumber(summary.expiringSoon)} detail="Access windows ending soon" tone="rose" />
            <MetricCard icon={CreditCard} label="Plans" value={formatNumber(plans.length)} detail="Configured candidate billing plans" />
            <MetricCard icon={Globe2} label="Regional controls" value="Global / Region / User" detail="Offer targeting scopes available" tone="slate" />
          </div>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
            <Panel title="Revenue by currency" subtitle="Paid payment transactions grouped for the last 30 days." icon={BadgeDollarSign}>
              <div className="space-y-3">
                {(summary.revenueByCurrency || []).map((item) => (
                  <div key={item.currency} className="rounded-[24px] border border-slate-100 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold text-slate-800">{item.currency}</span>
                      <span className="text-lg font-semibold text-slate-900">{formatMoney(item.amountCents30d, item.currency)}</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-500">{formatNumber(item.paidTransactions30d)} paid transactions</p>
                  </div>
                ))}
                {!summary.revenueByCurrency?.length && <EmptyState>No paid revenue recorded in the last 30 days.</EmptyState>}
              </div>
            </Panel>

            <Panel title="Plans" subtitle="Active plans are used for candidate access and offer creation." icon={FileText}>
              <div className="space-y-3">
                {plans.map((plan) => (
                  <div key={plan.id} className="rounded-[24px] border border-slate-100 bg-slate-50 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold text-slate-800">{plan.name}</span>
                      <Chip tone={plan.active ? 'emerald' : 'slate'}>{plan.active ? 'Active' : 'Inactive'}</Chip>
                    </div>
                    <p className="mt-1 text-sm text-slate-500">{formatMoney(plan.amountCents, plan.currency)} · {labelize(plan.interval)}</p>
                    {plan.nextPriceVersion && (
                      <p className="mt-1 text-xs text-amber-700">
                        Next: {formatMoney(plan.nextPriceVersion.amountCents, plan.nextPriceVersion.currency)} from {formatDateTime(plan.nextPriceVersion.effectiveAt)}
                      </p>
                    )}
                    <code className="mt-2 inline-flex rounded bg-slate-950 px-2 py-1 text-[11px] font-semibold text-white">{plan.code}</code>
                    {canManageBilling && (
                      <button
                        type="button"
                        onClick={() => setPricePlan(plan)}
                        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-cyan-200 bg-white px-3 py-2 text-sm font-semibold text-cyan-700 transition hover:bg-cyan-50"
                      >
                        <PencilLine size={15} />
                        Change price
                      </button>
                    )}
                  </div>
                ))}
                {!plans.length && <EmptyState>No billing plans configured.</EmptyState>}
              </div>
            </Panel>
          </div>
        </div>
      )}

      {activeTab === 'plans' && (
        <div className="space-y-5">
          <div className="flex flex-col gap-6">
            {plans.map((plan) => (
              <Panel
                key={plan.id}
                title={plan.name}
                subtitle={`${plan.code} · ${labelize(plan.interval)} billing`}
                icon={BadgeDollarSign}
                actions={canManageBilling && (
                  <button
                    type="button"
                    onClick={() => setPricePlan(plan)}
                    className="inline-flex items-center gap-2 rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    <PencilLine size={16} />
                    Change price
                  </button>
                )}
              >
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-[24px] border border-slate-100 bg-slate-50 p-6">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Active price</p>
                    <p className="mt-2 text-3xl font-bold tracking-tight text-slate-900">{formatMoney(plan.amountCents, plan.currency)}</p>
                    <p className="mt-1 text-xs font-medium text-slate-500">{plan.currentPriceVersion?.effectiveAt ? `Since ${formatDateTime(plan.currentPriceVersion.effectiveAt)}` : 'Current plan amount'}</p>
                  </div>
                  <div className="rounded-[24px] border border-slate-100 bg-slate-50 p-6">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Next price</p>
                    <p className="mt-2 text-2xl font-bold tracking-tight text-slate-900">
                      {plan.nextPriceVersion ? formatMoney(plan.nextPriceVersion.amountCents, plan.nextPriceVersion.currency) : 'None scheduled'}
                    </p>
                    <p className="mt-1 text-xs font-medium text-slate-500">
                      {plan.nextPriceVersion ? formatDateTime(plan.nextPriceVersion.effectiveAt) : 'No future change queued'}
                    </p>
                  </div>
                  <div className="rounded-[24px] border border-slate-100 bg-slate-50 p-6">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Provider</p>
                    <p className="mt-2 text-2xl font-bold tracking-tight text-slate-900">{labelize(plan.provider)}</p>
                    <p className="mt-1 break-all text-xs font-medium text-slate-500">{plan.providerPlanCode || 'No provider plan code'}</p>
                  </div>
                </div>

                <div className="mt-5">
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <History size={16} className="text-cyan-700" />
                    Price history
                  </div>
                  <div className="overflow-x-auto rounded-[24px] border border-slate-100">
                    <table className="min-w-full divide-y divide-slate-100 text-sm">
                      <thead className="bg-slate-50/50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                        <tr>
                          <th className="px-6 py-4">Amount</th>
                          <th className="px-6 py-4">Effective</th>
                          <th className="px-6 py-4">Status</th>
                          <th className="px-6 py-4">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(plan.priceVersions || []).map((version) => (
                          <tr key={version.id}>
                            <td className="px-6 py-4 font-semibold text-slate-900">{formatMoney(version.amountCents, version.currency)}</td>
                            <td className="px-6 py-4 text-slate-600">{formatDateTime(version.effectiveAt)}</td>
                            <td className="px-6 py-4"><Chip tone={statusTone(version.status)}>{labelize(version.status)}</Chip></td>
                            <td className="max-w-xs px-6 py-4 text-slate-500">{version.reason || 'Not recorded'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!plan.priceVersions?.length && <EmptyState>No price history recorded for this plan.</EmptyState>}
                </div>
              </Panel>
            ))}
          </div>
          {!plans.length && <EmptyState>No billing plans are configured.</EmptyState>}
        </div>
      )}

      {activeTab === 'candidates' && (
        <div className="space-y-5">
          <Panel title="Subscription search" subtitle="Search candidate subscriptions by name, email, region, and billing status." icon={Search}>
            <form onSubmit={submitFilters} className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_180px_180px_120px]">
              <input
                value={filterDraft.search}
                onChange={(event) => setFilterDraft((current) => ({ ...current, search: event.target.value }))}
                placeholder="Search candidate name, email, location, or nationality"
                className="h-11 rounded-[16px] border border-slate-200 px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
              />
              <input
                value={filterDraft.region}
                onChange={(event) => setFilterDraft((current) => ({ ...current, region: event.target.value }))}
                placeholder="Region"
                className="h-11 rounded-[16px] border border-slate-200 px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
              />
              <select
                value={filterDraft.status}
                onChange={(event) => setFilterDraft((current) => ({ ...current, status: event.target.value }))}
                className="h-11 rounded-[16px] border border-slate-200 bg-white px-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
              >
                <option value="">All statuses</option>
                <option value="paid">Paid</option>
                <option value="trial">Trial</option>
                <option value="extended">Extended</option>
                <option value="expired">Expired</option>
                <option value="inactive">Inactive</option>
              </select>
              <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800">
                <Search size={16} />
                Search
              </button>
            </form>
          </Panel>

          <section className="overflow-hidden rounded-[24px] border border-slate-100 bg-white shadow-sm">
            <div className="flex flex-col gap-2 border-b border-slate-100 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-[17px] font-semibold leading-tight text-slate-900">Subscriptions</h2>
                <p className="mt-1 text-xs font-medium text-slate-500">{formatNumber(pagination.total)} candidates match the selected filters</p>
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-slate-500">
                Page {pagination.page} of {pagination.totalPages}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50/50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-4">Candidate</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Expiry</th>
                    <th className="px-6 py-4">Plan / Payment</th>
                    <th className="px-6 py-4">Region</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {candidates.map((candidate) => (
                    <tr key={candidate.userId} className="align-top">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-slate-900">{candidate.name}</div>
                        <div className="mt-1 text-xs text-slate-500">{candidate.email}</div>
                        <div className="mt-2 text-xs text-slate-400">Candidate ref {formatReference(candidate.candidateId)}</div>
                      </td>
                      <td className="px-6 py-4">
                        <Chip tone={statusTone(candidate.billingStatus)}>{labelize(candidate.billingStatus)}</Chip>
                        {candidate.grant?.type && <div className="mt-2 text-xs text-slate-500">{labelize(candidate.grant.type)}</div>}
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        {formatDateTime(candidate.activeUntil)}
                        {candidate.daysUntilExpiry !== null && (
                          <div className={`mt-1 text-xs ${candidate.daysUntilExpiry <= 7 ? 'text-rose-600' : 'text-slate-500'}`}>
                            {candidate.daysUntilExpiry >= 0 ? `${candidate.daysUntilExpiry} days remaining` : 'Expired'}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-800">{candidate.plan?.name || 'No plan'}</div>
                        {candidate.latestPayment ? (
                          <div className="mt-1 text-xs text-slate-500">
                            {formatMoney(candidate.latestPayment.amountCents, candidate.latestPayment.currency)} · {labelize(candidate.latestPayment.status)}
                          </div>
                        ) : (
                          <div className="mt-1 text-xs text-slate-400">No payment recorded</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        <div className="flex items-start gap-2">
                          <MapPin size={14} className="mt-0.5 shrink-0 text-slate-400" />
                          <span>{candidate.region || 'Not recorded'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setDetailUserId(candidate.userId)}
                            className="rounded-[16px] border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                          >
                            Details
                          </button>
                          {canManageBilling && (
                            <>
                              <button
                                type="button"
                                onClick={() => setExtensionCandidate(candidate)}
                                disabled={!plans.length}
                                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Extend
                              </button>
                              <button
                                type="button"
                                onClick={() => setOfferSeedCandidate(candidate)}
                                disabled={!plans.length}
                                className="rounded-[16px] border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Offer
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!candidates.length && <div className="p-5"><EmptyState>No candidates match the selected billing filters.</EmptyState></div>}

            <div className="flex items-center justify-between border-t border-slate-100 px-6 py-5">
              <button
                type="button"
                onClick={() => changePage(Math.max(1, pagination.page - 1))}
                disabled={pagination.page <= 1}
                className="inline-flex items-center gap-2 rounded-[16px] border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
              >
                <ArrowLeft size={15} />
                Previous
              </button>
              <button
                type="button"
                onClick={() => changePage(Math.min(pagination.totalPages, pagination.page + 1))}
                disabled={pagination.page >= pagination.totalPages}
                className="inline-flex items-center gap-2 rounded-[16px] border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
              >
                Next
                <ArrowRight size={15} />
              </button>
            </div>
          </section>
        </div>
      )}

      {activeTab === 'offers' && (
        <Panel
          title="Billing offers"
          subtitle="Offers can target all candidates, a region, or one candidate. Creating an offer queues email and push notification records."
          icon={Gift}
          actions={canManageBilling && (
            <button
              type="button"
              onClick={() => setOfferSeedCandidate({})}
              disabled={!plans.length}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus size={16} />
              Create offer
            </button>
          )}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50/50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <tr>
                  <th className="px-6 py-4">Offer</th>
                  <th className="px-6 py-4">Scope</th>
                  <th className="px-6 py-4">Amount</th>
                  <th className="px-6 py-4">Recipients</th>
                  <th className="px-6 py-4">Validity</th>
                  <th className="px-6 py-4">Status</th>
                  {canManageBilling && <th className="px-6 py-4 text-right">Action</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {offers.map((offer) => (
                  <tr key={offer.id} className="align-top">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-slate-900">{offer.name}</div>
                      <div className="mt-1 text-xs text-slate-500">{offer.planName}</div>
                    </td>
                    <td className="px-6 py-4">
                      <Chip tone={offer.scope === 'global' ? 'cyan' : 'slate'}>{labelize(offer.scope)}</Chip>
                      <div className="mt-2 text-xs text-slate-500">{offer.region || offer.targetUserEmail || 'All active candidates'}</div>
                    </td>
                    <td className="px-6 py-4 font-semibold text-slate-900">
                      {formatMoney(offer.amountCents, offer.currency)}
                      <div className="mt-1 text-xs font-normal text-slate-500">{offer.durationDays} days access</div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      {formatNumber(offer.recipientCount)} notified
                      <div className="mt-1 text-xs text-slate-500">{formatNumber(offer.redeemedRecipientCount)} redeemed</div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{formatDateTime(offer.validUntil)}</td>
                    <td className="px-6 py-4"><Chip tone={statusTone(offer.status)}>{labelize(offer.status)}</Chip></td>
                    {canManageBilling && (
                      <td className="px-6 py-4 text-right">
                        <select
                          value={offer.status}
                          onChange={(event) => updateOfferStatus(offer, event.target.value)}
                          disabled={updatingOfferId === offer.id}
                          className="h-9 rounded-[16px] border border-slate-200 bg-white px-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100 disabled:opacity-60"
                        >
                          <option value="active">Active</option>
                          <option value="paused">Paused</option>
                          <option value="expired">Expired</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!offers.length && <EmptyState>No billing offers have been created.</EmptyState>}
        </Panel>
      )}

      {activeTab === 'payments' && (
        <Panel title="Transactions" subtitle="Latest candidate payment transactions and checkout status from the billing system." icon={ReceiptText}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50/50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <tr>
                  <th className="px-6 py-4">Candidate</th>
                  <th className="px-6 py-4">Reference</th>
                  <th className="px-6 py-4">Plan</th>
                  <th className="px-6 py-4">Amount</th>
                  <th className="px-6 py-4">Provider</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recentPayments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-slate-900">{payment.candidateName}</div>
                      <div className="mt-1 text-xs text-slate-500">{payment.candidateEmail}</div>
                    </td>
                    <td className="px-6 py-4 font-mono text-xs text-slate-600">{payment.reference}</td>
                    <td className="px-6 py-4 text-slate-700">{payment.planName || 'Plan unavailable'}</td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-slate-900">{formatMoney(payment.amountCents, payment.currency)}</div>
                      {payment.priceVersion && (
                        <div className="mt-1 text-xs text-slate-500">
                          Price version {formatReference(payment.priceVersion.id)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-600">{labelize(payment.provider)}</td>
                    <td className="px-6 py-4"><Chip tone={statusTone(payment.status)}>{labelize(payment.status)}</Chip></td>
                    <td className="px-6 py-4 text-slate-500">{formatDateTime(payment.paidAt || payment.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!recentPayments.length && <EmptyState>No payment transactions recorded.</EmptyState>}
        </Panel>
      )}

      {detailUserId && (
        <CandidateDetailModal
          userId={detailUserId}
          canManageBilling={canManageBilling}
          onClose={() => setDetailUserId(null)}
          onExtend={(candidate) => {
            setDetailUserId(null);
            setExtensionCandidate(candidate);
          }}
        />
      )}

      {canManageBilling && extensionCandidate && (
        <ExtensionModal
          candidate={extensionCandidate}
          plans={plans}
          onClose={() => setExtensionCandidate(null)}
          onSaved={refresh}
        />
      )}

      {canManageBilling && offerSeedCandidate && (
        <OfferModal
          plans={plans}
          seedCandidate={offerSeedCandidate.userId ? offerSeedCandidate : null}
          onClose={() => setOfferSeedCandidate(null)}
          onSaved={refresh}
        />
      )}

      {canManageBilling && pricePlan && (
        <PlanPriceModal
          plan={pricePlan}
          onClose={() => setPricePlan(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

export default BillingPage;
